import type { ReactElement } from 'react';
import { CampaignName } from 'src/common/enums/campaigns';
import type { AppFeatures } from 'src/common/interfaces/growthbook-generated-types';
import beardedITDadIcon from 'src/images/pngs/campaigns/influencers/bearded-it-dad-icon.png';
import toastyBrosIcon from 'src/images/pngs/campaigns/influencers/bros-icon.png';
import createdTechIcon from 'src/images/pngs/campaigns/influencers/created-tech-icon.png';
import DFIRIcon from 'src/images/pngs/campaigns/influencers/dfir-icon.png';
import ericParkerIcon from 'src/images/pngs/campaigns/influencers/eric-parker-icon.png';
import grantCollinsIcon from 'src/images/pngs/campaigns/influencers/grant-collins-icon.png';
import logicallyIcon from 'src/images/pngs/campaigns/influencers/logically-icon.png';
import madhatIcon from 'src/images/pngs/campaigns/influencers/madhat-icon.png';
import tylerRamsbeyIcon from 'src/images/pngs/campaigns/influencers/tyler-ramsbey-icon.png';
import { ReactComponent as ToastyBrosCampaignIcon } from 'src/images/svgs/campaigns/influencers/bros-icon.svg';
import { ReactComponent as CreatedTechCampaignIcon } from 'src/images/svgs/campaigns/influencers/created-tech-icon.svg';
import { ReactComponent as DFIRCampaignIcon } from 'src/images/svgs/campaigns/influencers/dfir-icon.svg';
import { ReactComponent as EricParkerCampaignIcon } from 'src/images/svgs/campaigns/influencers/eric-parker-icon.svg';
import { ReactComponent as GrantCollinsCampaignIcon } from 'src/images/svgs/campaigns/influencers/grant-collins-icon.svg';
import { ReactComponent as LogicallyCampaignIcon } from 'src/images/svgs/campaigns/influencers/logically-icon.svg';
import { ReactComponent as BeardedItDadCampaignIcon } from 'src/images/svgs/campaigns/influencers/pt-1-bearded-icon.svg';
import { ReactComponent as MadhatCampaignIcon } from 'src/images/svgs/campaigns/influencers/pt-1-madhat-icon.svg';
import { ReactComponent as TylerRamsbeyCampaignIcon } from 'src/images/svgs/campaigns/influencers/pt-1-tyler-icon.svg';
import { ReactComponent as FireshipCampaignIcon } from 'src/images/svgs/fireship/fireship-image.svg';
export type InfluencerCampaigns = CampaignName.MADHAT_2025 | CampaignName.THE_BEARDED_IT_DAD_2025 | CampaignName.TYLER_RAMSBEY_2025;
export type InfluencerSubscriptionCampaigns = CampaignName.FIRESHIP_2025 | CampaignName.LOGICALLY_2025 | CampaignName.DFIR_2025 | CampaignName.TOASTY_BROS_2025 | CampaignName.CREATED_TECH_2025 | CampaignName.ERIC_PARKER_2025 | CampaignName.GRANT_COLLINS_2025;
export const influencerCampaignValues = [CampaignName.MADHAT_2025, CampaignName.THE_BEARDED_IT_DAD_2025, CampaignName.TYLER_RAMSBEY_2025] as const;
export const influencerSubscriptionCampaignValues = [CampaignName.FIRESHIP_2025, CampaignName.LOGICALLY_2025, CampaignName.DFIR_2025, CampaignName.TOASTY_BROS_2025, CampaignName.CREATED_TECH_2025, CampaignName.GRANT_COLLINS_2025, CampaignName.ERIC_PARKER_2025] as const;
export type InfluencerAdditionalConfigs = Record<InfluencerCampaigns, {
  userFriendlyName: string;
  landingRedirect: string;
  detailsRedirect: string;
  urlToNavigate: string;
  page: string;
  details: string;
  pagesToShowCTA: string[];
  barCta: (userFriendlyName: string, percentageOff: number, isOnboarding: boolean) => ReactElement;
  featureFlag: keyof AppFeatures;
  percentageOff: number;
  icon: string;
  InfluencerIcon: React.FC<React.SVGProps<SVGSVGElement>>;
}>;
export type InfluencerSubscriptionAdditionalConfigs = Record<InfluencerSubscriptionCampaigns, {
  userFriendlyName: string;
  urlToNavigate: string;
  page: string;
  isGradient?: boolean;
  pagesToShowCTA: string[];
  barCta: (userFriendlyName: string, percentageOff: number, isOnboarding: boolean) => ReactElement;
  featureFlag: keyof AppFeatures;
  percentageOff: number;
  icon?: string;
  InfluencerIcon: React.FC<React.SVGProps<SVGSVGElement>>;
}>;
const DEFAULT_PAGES_TO_SHOW_CTA = ['/certification/junior-penetration-tester', '/welcome', '/dashboard/*'];
const DEFAULT_PAGES_TO_SHOW_CTA_SUBSCRIPTION = ['/', '/dashboard/*', ''];
const DEFAULT_PAGE = '/certification/junior-penetration-tester';
const DEFAULT_PAGE_SUBSCRIPTION = '/';
const DEFAULT_DETAILS = '/certification/junior-penetration-tester/details';
const DEFAULT_UTMS = 'utm_source=youtube&utm_medium=social&utm_campaign=';
const barCta = (userFriendlyName: string, percentageOff: number, isOnboarding: boolean): ReactElement => {
  if (isOnboarding) {
    return <>
        {userFriendlyName} viewers exclusive: <span>{percentageOff}% off</span> Jr Penetration Tester certification!
        Finish onboarding to claim your discount.
      </>;
  }
  return <>
      {userFriendlyName} viewers exclusive: <span>{percentageOff}% off</span> Jr Penetration Tester certification!
    </>;
};
const barCtaSubscription = (userFriendlyName: string, percentageOff: number, isOnboarding: boolean): ReactElement => {
  if (isOnboarding) {
    return <>
        {userFriendlyName} viewers exclusive: <span>{percentageOff}% off</span> annual subscriptions! Finish onboarding
        to Subscribe.
      </>;
  }
  return <>
      {userFriendlyName} viewers exclusive: <span>{percentageOff}% off</span> annual subscriptions!
    </>;
};
export const INFLUENCER_ADDITIONAL_CONFIGS: InfluencerAdditionalConfigs = {
  [CampaignName.MADHAT_2025]: {
    userFriendlyName: 'MadHat',
    landingRedirect: `${DEFAULT_PAGE}?${DEFAULT_UTMS}MadHat_PT1`,
    detailsRedirect: `${DEFAULT_DETAILS}?${DEFAULT_UTMS}MadHat_PT1`,
    urlToNavigate: '/login?source=madhat',
    page: DEFAULT_PAGE,
    details: DEFAULT_DETAILS,
    pagesToShowCTA: DEFAULT_PAGES_TO_SHOW_CTA,
    barCta,
    featureFlag: 'influencer-madhat',
    percentageOff: 25,
    icon: madhatIcon,
    InfluencerIcon: MadhatCampaignIcon
  },
  [CampaignName.THE_BEARDED_IT_DAD_2025]: {
    userFriendlyName: 'The Bearded I.T. Dad',
    landingRedirect: `${DEFAULT_PAGE}?${DEFAULT_UTMS}Dakota_PT1`,
    detailsRedirect: `${DEFAULT_DETAILS}?${DEFAULT_UTMS}Dakota_PT1`,
    urlToNavigate: '/login?source=thebeardeditdad',
    page: DEFAULT_PAGE,
    details: DEFAULT_DETAILS,
    pagesToShowCTA: DEFAULT_PAGES_TO_SHOW_CTA,
    barCta,
    featureFlag: 'influencer-the-bearded-it-dad',
    percentageOff: 25,
    icon: beardedITDadIcon,
    InfluencerIcon: BeardedItDadCampaignIcon
  },
  [CampaignName.TYLER_RAMSBEY_2025]: {
    userFriendlyName: 'Tyler Ramsbey',
    landingRedirect: `${DEFAULT_PAGE}?${DEFAULT_UTMS}Tyler_PT1`,
    detailsRedirect: `${DEFAULT_DETAILS}?${DEFAULT_UTMS}Tyler_PT1`,
    urlToNavigate: '/login?source=tylerramsbey',
    page: DEFAULT_PAGE,
    details: DEFAULT_DETAILS,
    pagesToShowCTA: DEFAULT_PAGES_TO_SHOW_CTA,
    barCta,
    featureFlag: 'influencer-tyler-ramsbey',
    percentageOff: 25,
    icon: tylerRamsbeyIcon,
    InfluencerIcon: TylerRamsbeyCampaignIcon
  }
};
export const INFLUENCER_SUBSCRIPTION_ADDITIONAL_CONFIGS: InfluencerSubscriptionAdditionalConfigs = {
  [CampaignName.FIRESHIP_2025]: {
    userFriendlyName: 'FireShip',
    page: `${DEFAULT_PAGE_SUBSCRIPTION}?${DEFAULT_UTMS}fireship02042025`,
    urlToNavigate: '/signup?source=fireship',
    pagesToShowCTA: DEFAULT_PAGES_TO_SHOW_CTA_SUBSCRIPTION,
    barCta: barCtaSubscription,
    featureFlag: 'fireship-2025',
    percentageOff: 20,
    InfluencerIcon: FireshipCampaignIcon
  },
  [CampaignName.LOGICALLY_2025]: {
    userFriendlyName: 'Logically',
    page: `${DEFAULT_PAGE_SUBSCRIPTION}?${DEFAULT_UTMS}Logically_725`,
    urlToNavigate: '/signup?source=logically',
    pagesToShowCTA: DEFAULT_PAGES_TO_SHOW_CTA_SUBSCRIPTION,
    barCta: barCtaSubscription,
    featureFlag: 'influencer-logically',
    percentageOff: 25,
    icon: logicallyIcon,
    InfluencerIcon: LogicallyCampaignIcon
  },
  [CampaignName.DFIR_2025]: {
    userFriendlyName: 'My DFIR',
    page: `${DEFAULT_PAGE_SUBSCRIPTION}?${DEFAULT_UTMS}dfir20250722`,
    urlToNavigate: '/signup?source=dfir',
    pagesToShowCTA: DEFAULT_PAGES_TO_SHOW_CTA_SUBSCRIPTION,
    barCta: barCtaSubscription,
    featureFlag: 'influencer-dfir',
    percentageOff: 25,
    icon: DFIRIcon,
    InfluencerIcon: DFIRCampaignIcon
  },
  [CampaignName.TOASTY_BROS_2025]: {
    userFriendlyName: 'Toasty Bros',
    page: `${DEFAULT_PAGE_SUBSCRIPTION}?${DEFAULT_UTMS}bros20250724`,
    urlToNavigate: '/signup?source=bros',
    pagesToShowCTA: DEFAULT_PAGES_TO_SHOW_CTA_SUBSCRIPTION,
    barCta: barCtaSubscription,
    featureFlag: 'influencer-toastybros',
    percentageOff: 25,
    icon: toastyBrosIcon,
    InfluencerIcon: ToastyBrosCampaignIcon
  },
  [CampaignName.CREATED_TECH_2025]: {
    userFriendlyName: 'Created Tech',
    page: `${DEFAULT_PAGE_SUBSCRIPTION}?${DEFAULT_UTMS}created_tech20250726`,
    urlToNavigate: '/signup?source=created_tech',
    pagesToShowCTA: DEFAULT_PAGES_TO_SHOW_CTA_SUBSCRIPTION,
    barCta: barCtaSubscription,
    featureFlag: 'influencer-createdtech',
    percentageOff: 25,
    icon: createdTechIcon,
    InfluencerIcon: CreatedTechCampaignIcon
  },
  [CampaignName.GRANT_COLLINS_2025]: {
    userFriendlyName: 'Grant Collins',
    page: `${DEFAULT_PAGE_SUBSCRIPTION}?${DEFAULT_UTMS}grant_20250716`,
    urlToNavigate: '/signup?source=grant',
    pagesToShowCTA: DEFAULT_PAGES_TO_SHOW_CTA_SUBSCRIPTION,
    barCta: barCtaSubscription,
    featureFlag: 'influencer-grant-col',
    percentageOff: 25,
    icon: grantCollinsIcon,
    InfluencerIcon: GrantCollinsCampaignIcon
  },
  [CampaignName.ERIC_PARKER_2025]: {
    userFriendlyName: 'Eric Parker',
    page: `${DEFAULT_PAGE_SUBSCRIPTION}?${DEFAULT_UTMS}eric_20250716`,
    urlToNavigate: '/signup?source=eric',
    pagesToShowCTA: DEFAULT_PAGES_TO_SHOW_CTA_SUBSCRIPTION,
    barCta: barCtaSubscription,
    featureFlag: 'influencer-eric-parker',
    percentageOff: 25,
    icon: ericParkerIcon,
    InfluencerIcon: EricParkerCampaignIcon
  }
};
export function isInfluencerCampaignKey(key: string): key is InfluencerCampaigns {
  return key in INFLUENCER_ADDITIONAL_CONFIGS;
}
export function isInfluencerSubscriptionCampaignKey(key: string): key is InfluencerSubscriptionCampaigns {
  return key in INFLUENCER_SUBSCRIPTION_ADDITIONAL_CONFIGS;
}